import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Linking, Platform, Image, Text, View, StyleSheet } from 'react-native';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import Svg from '../Svg';
import { formatPhoneNumber } from '../../utils/formatPhone';
import FilledButton from '../FilledButton';
import Header from './Header';
import MyScrollView from '../MyScrollView';
import PtfKeyboardAvoidingView from '../PtfKeyboardAvoidingView';
import MyFloatingLabelInput from '../form/MyFloatingLabelInput';

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  content: {
    padding: 16,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 720 : '100%',
    backgroundColor: Colors.white,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  innerContent: {
    width: 330,
    flex: 1,
    alignItems: 'center',
  },
  headerText: {
    fontSize: 20,
    fontWeight: '500',
    color: Platform.OS === 'web' ? Colors.mediumBlue : Colors.white,
  },
  logo: {
    resizeMode: 'contain',
    flexGrow: 2,
    flexShrink: 0,
    marginBottom: 8,
  },
  image: {
    width: 177,
    height: 142,
    display: Layout.window.height > 600 ? 'flex' : 'none',
  },
  row: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  welcomeText: {
    fontSize: 26,
    color: Colors.mediumBlue,
    flexGrow: 1,
    flexShrink: 0,
  },
  fullRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 15,
  },
  introText: {
    flexGrow: 1,
    flexShrink: 0,
    color: Colors.veryDarkGray,
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 14 : 16,
    width: Layout.isSmall ? 269 : 330,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  inputContainer: {
    marginTop: 24,
    marginBottom: 24,
  },
  emptyLogo: {
    width: 24,
  },
  submitButtonContainer: {
    width: 328,
    justifyContent: 'center',
  },
  linkContainer: {
    paddingHorizontal: 16,
    paddingBottom: 6,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  linkText: {
    fontSize: Layout.isSmall ? 10 : 16,
    marginBottom: 24,
    color: Colors.darkGreen,
    textDecorationLine: 'underline',
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  linkSeparator: {
    fontSize: 16,
    marginBottom: 24,
    color: Colors.darkGreen,
    fontWeight: '500',
    paddingHorizontal: 10,
  },
  smsDisclaimer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    alignItems: 'center',
  },
  smsDisclaimerText: {
    fontSize: 12,
    color: Colors.veryDarkGray,
    fontFamily: 'roboto-regular',
    textAlign: 'center',
  },
});

class SelectPhoneOrEmail extends Component {
  static propTypes = {
    translation: PropTypes.objectOf(PropTypes.any).isRequired,
    inputError: PropTypes.string,
    goNext: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  constructor(props) {
    super();
    this.state = {
      inputText: '',
      validInput: false,
    };
  }

  validate = (value) => {
    if (!value) return false;
    // very simple email, needs an @ and a .
    if (value.indexOf('@') !== -1 && value.indexOf('.') !== -1 && value.indexOf('.') < value.length - 2)
      return true;
    // phone must be number and of length 10
    return !isNaN(value) && value.length === 10;
  };

  submitForm = () => {
    const { inputText } = this.state;
    const { goNext } = this.props;
    let value = inputText.trim();
    if (value && value[0] === '(') {
      value = value.replace('(', '').replace(')', '').replace('-', '').replace(/ /g, '');
    }
    const isValid = this.validate(value);
    const isNumbers = /^\d+$/.test(value);
    if (!isValid) {
      this.setState({
        inputText: isNumbers ? formatPhoneNumber(value) : value,
        validInput: false,
      });
    } else {
      goNext(value);
      this.setState({ validInput: false });
    }
  };

  onChangeText = (text) => {
    const { actions, inputError } = this.props;
    if (inputError) actions.clearEmailOrPhoneError();
    let value = text.trim();
    // if starts with (, remove all phone formatting
    if (value && value[0] === '(') {
      value = value.replace('(', '').replace(')', '').replace('-', '').replace(/ /g, '');
    }
    const isValid = this.validate(value);
    const isNumbers = /^\d+$/.test(value);
    if (isNumbers) value = formatPhoneNumber(value);
    this.setState({ inputText: value, validInput: isValid });
  };

  render() {
    const { translation, goNext, goBack, inputError } = this.props;
    const { inputText, validInput } = this.state;
    const labels = {
      welcome: translation.welcome_to_plentiful,
      enter_email_phone: translation.enter_email_phone,
      email_or_phone: translation.email_or_phone,
      next: translation.fragment_registration_family_button_next,
      privacy: translation.privacy_policy,
      terms: translation.terms_conditions,
      sign_up: translation.sign_up,
    };

    const headerBackGround = {
      backgroundColor: Platform.OS === 'web' ? Colors.white : Colors.mediumBlue,
    };
    if (Platform.OS === 'web') {
      headerBackGround.boxShadow = '0px 0px 10px rgba(0, 0, 0, 0.1)';
      headerBackGround.zIndex = 1;
    }
    const logoSize = {
      width: Layout.isSmall ? 147 : 288,
      height: Layout.isSmall ? 56 : 110,
    };

    return (
      <PtfKeyboardAvoidingView style={{ flex: 1 }}>
        <MyScrollView
          containerStyle={{
            flexDirection: 'column',
            backgroundColor: Platform.OS === 'web' ? Colors.lightBackground : Colors.mediumBlue,
          }}
        >
          <Header title={labels.sign_up} goBack={goBack} />
          <View style={styles.contentContainer} testID="email-or-phone-form">
            <View style={styles.content}>
              <View style={styles.innerContent}>
                <View style={styles.row}>
                  <View style={styles.logo}>
                    <Svg
                      icon="logo_label_blue"
                      width={logoSize.width}
                      height={logoSize.height}
                      color={Colors.mediumBlue}
                    />
                  </View>
                  <Image style={styles.image} source={require('../../assets/images/sign_up/image_10.png')} />
                  <Text style={styles.welcomeText}>{labels.welcome}</Text>
                  <Text style={styles.introText}>{labels.enter_email_phone}</Text>

                  <View style={styles.inputContainer}>
                    <MyFloatingLabelInput
                      testID="phone-or-email-field"
                      label={labels.email_or_phone}
                      value={inputText}
                      onChangeText={this.onChangeText}
                      onSubmitEditing={this.submitForm}
                      width={328}
                      error={inputError ? translation[inputError] : ''}
                    />
                  </View>
                </View>
                <View style={styles.submitButtonContainer}>
                  <FilledButton
                    onPress={this.submitForm}
                    label={labels.next}
                    color={Colors.primaryColor}
                    full
                    textColor={Colors.white}
                    disabled={!validInput}
                  />
                </View>

                <View style={styles.smsDisclaimer}>
                  <Text style={styles.smsDisclaimerText}>{translation.sms_disclaimer}</Text>
                </View>

                <View style={styles.linkContainer}>
                  <Text
                    onPress={() => {
                      Linking.openURL(`https://www.plentifulapp.com/privacy`);
                    }}
                    style={[styles.linkText]}
                  >
                    {translation.privacy_policy}
                  </Text>
                  <Text style={styles.linkSeparator}>{'•'}</Text>
                  <Text
                    onPress={() => {
                      Linking.openURL(`https://www.plentifulapp.com/eula`);
                    }}
                    style={[styles.linkText]}
                  >
                    {translation.terms_conditions}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </MyScrollView>
      </PtfKeyboardAvoidingView>
    );
  }
}

export default SelectPhoneOrEmail;
